import type { Endpoints } from '@/generated/internal-api/Endpoints';
import { errorToast } from '@/helpers';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import type { ApiServiceProps } from '@/support/ApiService';
import {
  type BenchlingDelegatedFailure,
  benchlingDelegatedFailure,
  benchlingDelegatedFailureMessage,
  type BenchlingDelegatedFailureReason,
  benchlingLinkDisabledReason,
  BenchlingLinkService,
  linkStateOf,
  showBenchlingLinkRequiredToast,
  useBenchlingLink,
} from '@/support/benchlingLink/BenchlingLink.utils';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { useRef } from 'react';

export type BenchlingDelegatedCallResult<Endpoint extends keyof Endpoints> =
  | { ok: true; body: Endpoints[Endpoint]['response'] }
  | { ok: false; failure: BenchlingDelegatedFailure | undefined };

export interface UseBenchlingDelegatedCallProps<Endpoint extends keyof Endpoints> {
  endpoint: Endpoint;
  /** Completes "permission to …" in the failure messages, e.g. "create this worklist". */
  actionLabel: string;
  /** Sentry slug for failures worth capturing. */
  slug: string;
  /** Runs after the default handling for that reason, for what only the caller can do. */
  onFailure?: Partial<Record<BenchlingDelegatedFailureReason, () => void>>;
}

export interface UseBenchlingDelegatedCallOutput<Endpoint extends keyof Endpoints> {
  invoke: (
    props?: Pick<ApiServiceProps<Endpoint>, 'path' | 'query' | 'body'>
  ) => Promise<BenchlingDelegatedCallResult<Endpoint>>;
  loading: boolean;
  /** True unless the user holds a usable link. The server still decides; this saves a doomed click. */
  disabled: boolean;
  disabledReason: string | undefined;
}

/**
 * Calls an endpoint that acts in Benchling as the signed-in user, handling the failures every such
 * call shares so a caller only deals with success.
 *
 * Never invoked on mount: a delegated call creates things in Benchling under the user's name, so it
 * must only ever follow a user action. Resolves rather than rejects, with the failure already
 * reported, and keeps the app's link state in step with what the call revealed.
 */
export const useBenchlingDelegatedCall = <Endpoint extends keyof Endpoints>({
  endpoint,
  actionLabel,
  slug,
  onFailure,
}: UseBenchlingDelegatedCallProps<Endpoint>): UseBenchlingDelegatedCallOutput<Endpoint> => {
  const state = useBenchlingLink();
  // A ref, not the loading state: two clicks in one frame both read loading as false, and each would
  // create its own copy in Benchling.
  const inFlight = useRef(false);
  const { invoke: invokeApi, loading } = useApiHook({
    endpoint,
    options: {
      invokeOnInit: false,
      // preserveOriginalError keeps the response on the thrown error; wrapped in an InVivoError the
      // endpoint's reason code is no longer reachable.
      onError: { throw: true, preserveOriginalError: true, toast: false, capture: false, slug },
    },
  });

  const reportFailure = (error: unknown): BenchlingDelegatedFailure | undefined => {
    const failure = benchlingDelegatedFailure(error);

    if (!_notNil(failure)) {
      errorToast(`Could not ${actionLabel}, please try again later.`);
      ExceptionHandler.captureException(new InVivoError(`Could not ${actionLabel}`, { cause: error, slug }));
      return undefined;
    }

    if (failure.reason === 'benchling_link_required') {
      // A user with no link at all could not have reached an enabled action, so this is a link that
      // stopped working since the app loaded.
      const expired = BenchlingLinkService.get().status !== 'NOT_LINKED';
      if (expired) {
        BenchlingLinkService.set(linkStateOf('LINK_EXPIRED'));
      }
      showBenchlingLinkRequiredToast(expired);
    } else {
      errorToast(benchlingDelegatedFailureMessage(failure, actionLabel));
      if (failure.reason === 'benchling_request_rejected') {
        ExceptionHandler.captureException(
          new InVivoError(`Benchling rejected: ${actionLabel}`, { cause: error, slug })
        );
      }
    }

    onFailure?.[failure.reason]?.();
    return failure;
  };

  const invoke: UseBenchlingDelegatedCallOutput<Endpoint>['invoke'] = async (props) => {
    if (inFlight.current) {
      return { ok: false, failure: undefined };
    }
    inFlight.current = true;
    try {
      const response = await invokeApi(props);
      if (response.type !== 'success') {
        throw response.error;
      }
      // Read now rather than from the render that started the call, which the await may have outlived.
      const known = BenchlingLinkService.get();
      if (known.status !== 'LINKED') {
        BenchlingLinkService.set(linkStateOf('LINKED', known.refreshTokenExpiresAt));
      }
      return { ok: true, body: response.body };
    } catch (error) {
      const cause = (error as { error?: unknown })?.error ?? error;
      if (!notAborted(cause)) {
        return { ok: false, failure: undefined };
      }
      return { ok: false, failure: reportFailure(cause) };
    } finally {
      inFlight.current = false;
    }
  };

  const disabledReason = benchlingLinkDisabledReason(state.status, actionLabel);
  const disabled = _notNil(disabledReason);

  return { invoke, loading, disabled, disabledReason };
};
